import { createORPCClient } from "@orpc/client";
import {
  ContractProcedure,
  eventIterator,
  type AnyContractProcedure,
  type ContractRouterClient,
} from "@orpc/contract";
import { chatContract, type ChatContract } from "@typedoc/contracts/chat/contract";
import { z } from "zod";
import { contractLink, type ContractCallContext } from "../contract-client";
import { knownFields } from "../known-fields";

export type ChatApiClient = ContractRouterClient<ChatContract, ContractCallContext>;

/**
 * The contract as this client reads it. Path, input and errors stay the contract's own; only the
 * output schema is relaxed, in two ways, so a newer server cannot lock an open page out:
 *
 * - A response's strict schema keeps what it knows and leaves out a field it does not
 *   (`knownFields`): a server that adds a key to a message, as a chat's sources were, would
 *   otherwise stop every tab opened before it from opening that chat until the tab reloaded. A
 *   value that is wrong, and not only newer, is still refused.
 * - The stream's frames are not checked against the contract's strict schema at all: a newer
 *   server may send an event this build has never heard of, and `parseStreamFrame` keeps the events
 *   it knows where a strict check would end the stream.
 *
 * This reaches the tabs that open on a build with it. A tab already open on an older build cannot
 * be changed, and an addition to the contract still reaches it as it always has.
 */
function readableContract(contract: ChatContract): ChatContract {
  const procedures: Record<string, AnyContractProcedure> = {};
  for (const [name, procedure] of Object.entries(contract)) {
    const definition = procedure["~orpc"];
    if (name === "stream") {
      procedures[name] = new ContractProcedure({
        ...definition,
        outputSchema: eventIterator(z.unknown()),
      });
    } else if (definition.outputSchema === undefined) {
      procedures[name] = procedure;
    } else {
      procedures[name] = new ContractProcedure({
        ...definition,
        outputSchema: knownFields(definition.outputSchema as z.ZodType),
      });
    }
  }
  return procedures as unknown as ChatContract;
}

const readable = readableContract(chatContract);

/** The Chats API through its contract; each call names its region and token. */
export const chatApiClient: ChatApiClient = createORPCClient(contractLink(readable));
